import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Row, Col, Button, Typography, Switch, Space, Select, Input, DatePicker, Tooltip } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useHistory, useParams, useLocation } from "react-router-dom";
import SaveMenu from "../../components/SaveMenu";
import useModel from "../../hooks/useModel";
import { charterModelData, routeModelData } from "../../services/ModelService";
import { Charter } from "../../model/charter";
import LoadMessage from "../../components/LoadMessage";
import RoutesViewer from "../../components/RoutesViewer";
import useAllStops from "../../hooks/useAllStops";
import useAllRoutes from "../../hooks/useAllRoutes";
import useAllSchedules from "../../hooks/useAllSchedules";
import EditRouteDetails from "../../components/EditRouteDetails";
import useAllVehicleTypes from "../../hooks/useAllVehicleType";
import { Calendar, Services, User, Timer, Bus } from "../../components/Icons";
import { getCachedPathDebounced } from "../../libs/pathLib";
import { isEmpty, last } from "lodash";
import { getStartDepotDepartureTime, getEndDepotArrivalTime, formatDateTime } from "../../libs/routes-lib";
import dayjs from "../../dayjs";
import { DATE_STRING } from "../../model/busRoute";

const { Text, Title } = Typography;

const BookingCharter = ({ menu = true, onSave, initialInstance }) => {
  const history = useHistory();
  const location = useLocation();
  const { allStops, setAllStops, allDepots } = useAllStops();
  const { allRoutes } = useAllRoutes();
  const { allSchedules } = useAllSchedules();
  const { allVehicleTypes, allVehicleTypesArray } = useAllVehicleTypes();

  const [totalDistance, setTotalDistance] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const { routeId: selectedRouteId } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const editFromQuery = queryParams.get('edit') === 'true';

  const {
    model: charter,
    setModel: setCharter,
    initialModelRef: initialCharterRef,
    mode,
    setMode,
    controls,
  } = useModel({
    modelService: charterModelData,
    initialInstance: initialInstance || new Charter({ charterId: "_" }),
  });

  useEffect(() => {
    if (editFromQuery) {
      setMode({ edit: true });
    }
  }, [editFromQuery, setMode]);

  const handleSave = async () => {
    try {
      charter.itinerary.forEach(route => {
        // Remove any stops/stopTimes that don't have valid coordinates
        route.stops = route.stops.filter(stop => {
          return stop.lon && stop.lat && stop.stopId && stop.stopName;
        });
        route.stopTimes = route.stopTimes.filter(stopTime => {
          if (!stopTime.stopId) return false;
          const stop = route.stops.find(s => s.stopId === stopTime.stopId);
          return stop != null;
        });
        // Also clean up any services
        route.services.forEach(trip => {
          if (trip.stops) {
            trip.stops = trip.stops.filter(stop => {
              return stop.lon && stop.lat && stop.stopId && stop.stopName;
            });
          }
          trip.stopTimes = trip.stopTimes.filter(stopTime => {
            if (!stopTime.stopId) return false;
            const stop = route.stops.find(s => s.stopId === stopTime.stopId);
            return stop != null;
          });
        });
      });
      await routeModelData.save(charter.itinerary.map(route => route.clone()));
      await charterModelData.save(charter);
      setMode({ edit: false });
      // history.push(`/bookings/${bookingId}`);
    } catch (error) {
      console.error("Error saving charter:", error);
    }
  };

  const handleBack = () => {
    history.push(`/bookings/${charter.charterId}`);
  };

  const setRouteHistory = useCallback(
    (oldRoute, newRoute) => {
      if (typeof newRoute === "function") {
        newRoute = newRoute(oldRoute);
      }
      setCharter((charter) => {
        return new Charter({
          ...charter,
          itinerary: charter.itinerary.map((route) => {
            if (route.routeId === newRoute.routeId) {
              return newRoute;
            }
            return route;
          }),
        });
      });
    },
    [setCharter]
  );

  const selectedRoute = useMemo(() => {
    const route = charter.itinerary.find((route) => route.routeId === selectedRouteId);
    return route;
  }, [charter, selectedRouteId]);

  const setRoute = (route, field, value) => {
    setCharter((charter) => {
      const updatedCharter = charter.clone();
      updatedCharter.itinerary.forEach(route => {
        if (route.routeId === selectedRouteId) {
          route[field] = value;
        }
      });
      return updatedCharter;
    });
  }

  const getValidationIssues = () => {
    // TODO: Implement validation issues
    return []
  }

  // Add a helper function to format seconds into days, hours, minutes
  const formatDuration = (seconds) => {
    if (!seconds) return "0m";

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.ceil((seconds % 3600) / 60);

    let result = "";
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}hr `;
    result += `${minutes}m`;

    return result;
  };

  // Calculate actual duration between start and end depots
  const getActualTripDuration = () => {
    const totalSeconds = selectedRoute?.getTotalDuration() || 0;

    if (totalSeconds > 0) {
      const days = Math.floor(totalSeconds / 86400); // 86400 = 24 * 60 * 60
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.ceil((totalSeconds % 3600) / 60);

      return { days, hours, minutes };
    }

    const startTime = getStartDepotDepartureTime(selectedRoute, selectedRoute.stops[0]);
    const endTime = getEndDepotArrivalTime(selectedRoute, last(selectedRoute.stops));

    if (!startTime || !endTime) return { days: 0, hours: 0, minutes: 0 };

    // Calculate duration in seconds
    const durationInSeconds = endTime?.diff(startTime, 'second');

    const days = Math.floor(durationInSeconds / 86400); // 86400 = 24 * 60 * 60
    const hours = Math.floor((durationInSeconds % 86400) / 3600);
    const minutes = Math.ceil((durationInSeconds % 3600) / 60);

    return { days, hours, minutes };
  };

  // Calculate total wait time at stops
  const getTotalWaitTime = (route) => {
    if (!route || !route.stops || route.stops.length === 0) return 0;

    let totalWaitTime = 0;
    let hasValidWaitTimes = false;

    route.stops.forEach((stop, index) => {
      // Skip first and last stops if they have invalid values
      if (stop.arriveSecs !== undefined && stop.departSecs !== undefined) {
        const waitTime = stop.departSecs - stop.arriveSecs;
        if (waitTime > 0) {
          totalWaitTime += waitTime;
          hasValidWaitTimes = true;
          console.log(`Stop ${index} (${stop.stopName}) wait time: ${waitTime}s`);
        }
      }
    });

    // If we have route.stopTimes, use that as a fallback
    if (!hasValidWaitTimes && route.stopTimes && route.stopTimes.length > 0) {
      route.stopTimes.forEach((stopTime, index) => {
        if (stopTime.arriveTime !== undefined && stopTime.departTime !== undefined) {
          const waitTime = stopTime.departTime - stopTime.arriveTime;
          if (waitTime > 0) {
            totalWaitTime += waitTime;
            hasValidWaitTimes = true;
          }
        }
      });
    }

    console.log(`Total wait time for route ${route.routeId}: ${totalWaitTime}s`);
    return totalWaitTime;
  };

  // Also add a useEffect to make the wait time calculation more reactive
  useEffect(() => {
    if (selectedRoute) {
      // Force a re-calculation of wait times when the route changes
      const waitTime = getTotalWaitTime(selectedRoute);
      console.log(`Initial wait time calculation: ${waitTime}s`);
    }
  }, [selectedRoute]);

  const routeName = useMemo(() => {
    if(!selectedRoute) return "";
    return selectedRoute.stops && selectedRoute.stops.length > 0 && selectedRoute.stops[0]?.stopName && selectedRoute.stops[selectedRoute.stops.length - 1]?.stopName ? `${selectedRoute.routeName} - ${selectedRoute.stops[0].stopName} → ${selectedRoute.stops[selectedRoute.stops.length - 1].stopName}` : selectedRoute.routeName;
  }, [selectedRoute?.routeName, selectedRoute?.stops]);

  return (
    <div className={`charter-details w-secondary-menu ${mode.edit ? 'edit-mode' : ''}`}>
      {charter && selectedRoute ? (
        <>
          <SaveMenu
            save={handleSave}
            editMode={mode.edit}
            id={charter.charterId}
            setEditMode={(edit) => setMode({ edit })}
            controls={controls}
            updated={controls.canUndo}
            modelInstance={charter}
            initialInstanceRef={initialCharterRef}
            listUrl={`/bookings/${charter.charterId}?${editFromQuery ? 'edit=true' : ''}`}
            modelService={charterModelData}
            validFn={() => charter.isValid()}
            closeView={true}
          />
          <Card className="card-main map-holder-wrap no-pad d-flex">

            <Row gutter={[20, 20]}>
              <Col xs={24} style={{ padding: '15px 30px' }}>
                {mode.edit ? <div className="d-flex align-items-center" style={{ gap: '10px', marginBottom: '6px' }}>
                  <Input style={{ width: 'auto', minWidth: 'auto', fontSize: 16, fontWeight: 500 }}
                    placeholder="Enter route name"
                    value={selectedRoute?.routeName}
                    onChange={(e) => setRoute(selectedRoute, 'routeName', e.target.value)}
                  />
                  <Title level={4} style={{ fontSize: 18, marginBottom: 0 }}>{selectedRoute.stops?.[0]?.stopName} {selectedRoute.stops?.length > 1 ? '→' : ''} {selectedRoute.stops.length > 1 && selectedRoute.stops?.[selectedRoute.stops.length - 1]?.stopName}</Title>
                </div> : <Title level={4} style={{ fontSize: 18 }}>{routeName}</Title>}
                <Row gutter={[20, 0]}>
                  <Col xs={12} lg={4} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <Calendar />
                      {charter.charterType === "recurring" ?
                        <div className="w-100">
                          <strong>{allSchedules[charter.scheduleId]?.scheduleName}</strong>
                        </div> :
                        <div className="w-100">
                          <strong>{charter.dates?.map(date => date?.format("DD/MM/YYYY")).join(', ')}</strong>
                        </div>}
                    </div>
                  </Col>
                  <Col xs={12} lg={3} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <User />
                      <div className="w-100">
                        {mode.edit ? (
                          <Input style={{ width: '50px' }}
                            value={selectedRoute.passengerCount || charter.passengerCount || 0}
                            onChange={(e) => setRoute(selectedRoute, 'passengerCount', e.target.value)}
                          />
                        ) : (
                          <strong>{selectedRoute.passengerCount || charter.passengerCount || 0}</strong>
                        )} pax
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} lg={3} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <Services />
                      <div className="w-100">
                        <strong>{Math.ceil((selectedRoute?.getTotalDistance() || 0) / 1000)}</strong> kms <Tooltip title={
                          <div>
                            <div>Total kilometers for this charter</div>
                            {(selectedRoute?.startDepot || selectedRoute?.endDepot) && selectedRoute?.stops?.length > 0 && (
                              <>
                                <div>In-service: {Math.ceil((selectedRoute?.distance || 0) / 1000)} kms</div>
                                <div>Dead-run: {Math.ceil(((selectedRoute?.getTotalDistance() || 0) - (selectedRoute?.distance || 0)) / 1000)} kms</div>
                              </>
                            )}
                          </div>
                        }><InfoCircleOutlined style={{ fontSize: '14px' }} /></Tooltip>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} lg={3} className="d-flex align-items-center">
                    <div className="icon-info-text">
                      <Timer />
                      <div className="w-100">
                        {(() => {
                          const duration = getActualTripDuration();
                          return (
                            <>
                              {duration.days > 0 && <><strong>{duration.days}</strong>d </>}
                              <strong>{duration.hours}</strong>hr
                              <strong> {duration.minutes}</strong>m
                              <Tooltip title={
                                <div>
                                  <div>Total duration breakdown:</div>
                                  {selectedRoute?.startDepot && selectedRoute?.stops?.length > 0 && (
                                    <div>Start dead-run: {formatDuration(selectedRoute?.startDepotDuration || 0)}</div>
                                  )}
                                  <div>In-service: {formatDuration((selectedRoute?.getTotalDuration() || 0) -
                                    (selectedRoute?.startDepotDuration || 0) -
                                    (selectedRoute?.endDepotDuration || 0) -
                                    (getTotalWaitTime(selectedRoute)))}</div>
                                  <div>Layover: {formatDuration(getTotalWaitTime(selectedRoute))}</div>
                                  {selectedRoute?.endDepot && selectedRoute?.stops?.length > 0 && (
                                    <div>End dead-run: {formatDuration(selectedRoute?.endDepotDuration || 0)}</div>
                                  )}
                                  <div style={{ borderTop: '1px solid #d9d9d9', marginTop: '5px', paddingTop: '5px' }}>
                                    <strong>TOTAL: {formatDuration(selectedRoute?.getTotalDuration() || 0)}</strong> ({((selectedRoute?.getTotalDuration() || 0) / 3600).toFixed(2)} hrs)
                                  </div>
                                </div>
                              }><InfoCircleOutlined style={{ fontSize: '14px', marginLeft: '5px' }} /></Tooltip>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row gutter={[20, 20]}>
              <Col xs={24}>
                {mode.edit ? (
                  <EditRouteDetails
                    replacePageTitle={false}
                    charter={charter}
                    allRoutes={allRoutes}
                    allStops={{ ...allStops, ...allDepots }}
                    setAllStops={setAllStops}
                    route={selectedRoute}
                    setRouteHistory={(newRoute) => setRouteHistory(selectedRoute, newRoute)}
                    useSimple={true}
                  />
                ) : (
                  <RoutesViewer
                    routes={allRoutes}
                    allSchedules={allSchedules}
                    allStops={{ ...allStops, ...allDepots }}
                    filteredRoutes={[selectedRoute]}
                    fetching={!allStops}
                    verifiedOnly={false}
                    allowComments={false}
                    showComments={false}
                    showWpSelector={true}
                    expand={true}
                    charter={charter}
                    useSimple={true}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </>
      ) : (
        <LoadMessage message="Loading Charter Details..." />
      )}
    </div>
  );
};

export default BookingCharter;
